import { searchClient } from "@algolia/client-search";
import { debounce } from "perfect-debounce";
import { storeToRefs } from "pinia";
import { useSearchStore } from "~/store/search";
import type StatamicCollectionRequest from "~/types/requests/StatamicCollectionRequest";

export function useSearch() {
    const router = useRouter();
    const route = useRoute();
    const config = useRuntimeConfig();

    // Get store data
    const {
        pageMounted,
        result,
        loading,
        selectedState,
        selectedFilters,
        selectedSortBy,
        selectedPageLimit,
        pageNum,
        searchText,
    } = storeToRefs(useSearchStore());
    searchText.value = route.query.search ? String(route.query.search) : searchText.value;

    const sortTypes = [
        { id: `${config.public.ALGOLIA_PREFIX}products_title_asc`, value: "A - Z" },
        { id: `${config.public.ALGOLIA_PREFIX}products_title_desc`, value: "Z - A" },
        { id: `${config.public.ALGOLIA_PREFIX}products_price_asc`, value: "Price: Low to High" },
        { id: `${config.public.ALGOLIA_PREFIX}products_price_desc`, value: "Price: High to Low" },
    ];

    const pageLimits = [
        { id: "20", value: "View 20 per page" },
        { id: "40", value: "View 40 per page" },
        { id: "60", value: "View 60 per page" },
    ];

    // --- Computed ---
    const routeQuery = computed(() => {
        const groupFiltered: Record<string, string[]> = {};
        // Loop through all selected filters. Split the string as key/value, then group them.
        selectedFilters.value.forEach((val) => {
            const ary = val.split(":");
            if (ary.length == 2) {
                if (!groupFiltered[ary[0]]) {
                    groupFiltered[ary[0]] = [];
                }
                groupFiltered[ary[0]].push(ary[1]);
            }
        });

        const query: Record<string, any> = {};
        // Add search & sort as well
        if (searchText.value) {
            query["search"] = searchText.value;
        }
        if (route.query.sortBy || selectedSortBy.value) {
            query["sortBy"] = selectedSortBy.value ?? route.query.sortBy;
        }
        if (route.query.limit || selectedPageLimit.value) {
            query["limit"] = selectedPageLimit.value ?? route.query.limit;
        }

        for (const [key, value] of Object.entries(groupFiltered)) {
            query[key] = value.join(","); // Algolia uses ',' as separator
        }

        return query;
    });

    const selectedStateChanged = computed(() => {
        if (!selectedState.value) {
            return false;
        }

        return !route.path.includes(`/state/${selectedState.value}/`.toLowerCase());
    });

    const searchFilters = computed(() => {
        const filters = ["status:published"];

        if (selectedState.value) {
            filters.push("states:" + selectedState.value);
        }

        if (routeQuery.value?.grades) {
            const grades = routeQuery.value.grades.split(",").map((val: string) => {
                return "grades:" + val;
            });
            filters.push(`(${grades.join(" OR ")})`);
        }
        if (routeQuery.value?.subjects) {
            const subjects = routeQuery.value.subjects.split(",").map((val: string) => {
                return "subjects:" + val;
            });
            filters.push(`(${subjects.join(" OR ")})`);
        }
        if (routeQuery.value?.series) {
            const series = routeQuery.value.series.split(",").map((val: string) => {
                return "series:" + val;
            });
            filters.push(`(${series.join(" OR ")})`);
        }

        return filters.join(" AND ");
    });

    const doAlgoliaSearch = computed(() => {
        return !!searchText.value;
    });

    const products = computed(() => {
        if (doAlgoliaSearch.value && result.value?.hits) {
            // Copy object to avoid mutating the original object
            return JSON.parse(JSON.stringify(result.value?.hits ?? []));
        }

        // Statamic was called
        return result.value?.data ?? [];
    });

    // --- Functions ---
    async function handleNavigateTo() {
        let url = `/products`;
        if (selectedState.value) {
            url = `/products/state/${selectedState.value.toLowerCase()}`;
        }

        url += `/page/${pageNum.value}`;

        await navigateTo({
            path: url,
            query: routeQuery.value,
        });
    }

    function setInitialUrlParams() {
        router.replace({
            query: routeQuery.value,
        });
    }

    async function performAlgoliasSearch(viewPerPage: number) {
        const indexName = selectedSortBy.value ? selectedSortBy.value : `${config.public.ALGOLIA_PREFIX}products`;

        try {
            const algoliaClient = searchClient(
                config.public.ALGOLIA_APPLICATION_ID as string,
                config.public.ALGOLIA_API_KEY as string,
            );
            const searchResponse = await algoliaClient.search([
                {
                    indexName,
                    params: {
                        query: routeQuery.value.search ?? selectedState.value ?? null,
                        hitsPerPage: viewPerPage,
                        filters: searchFilters.value,
                        page: pageNum.value ? parseInt(pageNum.value) - 1 : 0, // Page start at 0
                    },
                },
            ]);

            result.value = searchResponse.results[0] ?? [];
        } catch (error) {
            console.error("Algolia search error:", error);
            result.value = [];
        }
    }

    async function performStatamicSearch(viewPerPage: number) {
        const params: { [k: string]: any } = {};
        params["filter[status]"] = "published";
        params["limit"] = viewPerPage;
        params["page"] = pageNum.value;
        params["sort"] = "-featured,title";
        params["fields"] =
            "c_id,c_slug,coursewave_product_id,isbn,featured" +
            ",title,price_base,price_bulk,qty_25_minimum" +
            ",color_option,price_color_base,price_color_bulk,digital_option,price_digital" + // Need this base on the product type (color, b&w, or digital only)
            ",photo_sm,photo_sm_2x,photo,photo_2x,photo_lg,photo_lg_2x" +
            ",photo_xl,photo_xl_2x,photo_2xl,photo_2xl_2x" +
            ",photo_3xl,photo_3xl_2x,features";
        if (routeQuery.value.sortBy) {
            params["sort"] = routeQuery.value.sortBy;
        }
        if (selectedState.value !== "") {
            params["filter[states:contains]"] = selectedState.value.toLowerCase();
            // sort by state_specific so that state-specific titles show up at the top of the list
            params["sort"] = "state_specific,-featured";
        }
        if (routeQuery.value.search) {
            params["filter[title:contains]"] = routeQuery.value.search;
        }
        if (routeQuery.value?.grades) {
            params["filter[taxonomy:grades]"] = routeQuery.value.grades;
        }
        if (routeQuery.value?.subjects) {
            params["filter[taxonomy:subjects]"] = routeQuery.value.subjects;
        }
        if (routeQuery.value?.series) {
            params["filter[taxonomy:series]"] = routeQuery.value.series;
        }

        const searchEndpoint = config.public.STATAMIC_API + "/collections/products/entries";
        //const key = btoa(JSON.stringify(params));
        const response = await $fetch<StatamicCollectionRequest>(searchEndpoint, {
            //key,
            params: params,
        }).catch((error) => {
            console.error(error);
        });

        result.value = response;
    }

    async function fetchResult() {
        try {
            loading.value = true;

            let viewPerPage = parseInt(pageLimits.map((a) => a.id)[0]);
            if (selectedPageLimit.value && pageLimits.map((a) => a.id).includes(selectedPageLimit.value)) {
                viewPerPage = parseInt(selectedPageLimit.value);
            }

            if (doAlgoliaSearch.value) {
                // I don't think Algolia search has something like useFetch() to watch for changes. This will do for now.
                await performAlgoliasSearch(viewPerPage);
            } else {
                await performStatamicSearch(viewPerPage);
            }

            // Delay setting loading to false to prevent flickering
            if (import.meta.client) {
                setTimeout(() => {
                    loading.value = false;
                }, 500);
            } else {
                loading.value = false;
            }
        } catch (error) {
            console.error(error);
            loading.value = false;
        }
    }

    const debounceFetchResult = debounce(async () => {
        pageNum.value = "1"; // Reset page number to 1 since filters have changed
        await fetchResult();
        await handleNavigateTo();
    }, 1000);

    // Watch these data variables. Auto reset page and update result.
    watch(selectedPageLimit, (newVal, oldVal) => {
        if (!pageMounted.value) return;
        debounceFetchResult();
    });
    watch(selectedSortBy, (newVal, oldVal) => {
        if (!pageMounted.value) return;
        debounceFetchResult();
    });
    watch(searchFilters, async (newVal, oldVal) => {
        if (!pageMounted.value) return;
        debounceFetchResult();
    });
    watch(searchText, async (newVal, oldVal) => {
        if (!pageMounted.value) return;
        debounceFetchResult();
    });

    return {
        result,
        loading,
        pageNum,
        searchText,
        selectedState,
        selectedFilters,
        sortTypes,
        pageLimits,
        selectedSortBy,
        selectedPageLimit,
        selectedStateChanged,
        searchFilters,
        setInitialUrlParams,
        handleNavigateTo,
        fetchResult,
        debounceFetchResult,
        doAlgoliaSearch,
        products,
    };
}
